import React from 'react';
import { ArrowRight, Lightbulb } from 'lucide-react';
import JiraKeyChip from '../JiraKeyChip';
import { PROBLEM_LABEL, PROBLEM_TONE } from '../effort/types';
import { FlowItem, NamingItem, NoTimeItem, PlanningData, PlanningItem, TimelogData, TimelogEntryItem } from './types';
import * as fmt from '../../lib/format';

// A ticket line: its key, its title, a line of context, and what to do about it. Own items only.
function Row({ keyText, url, title, meta, children, hint }: { keyText: string; url: string; title: string | null; meta: React.ReactNode; children?: React.ReactNode; hint?: string | null }) {
  return (
    <li className="rounded-xl bg-white p-3.5 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <JiraKeyChip issueKey={keyText} url={url} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{title ?? 'Untitled'}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">{meta}</div>
      {children}
      {hint && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-blue-50/70 px-2.5 py-1.5 text-xs text-blue-900 ring-1 ring-inset ring-blue-100">
          <Lightbulb className="mt-px h-3.5 w-3.5 shrink-0 text-blue-600" aria-hidden />
          <span>{hint}</span>
        </p>
      )}
    </li>
  );
}

const Dot = () => <span aria-hidden>·</span>;

export function NamingList({ items }: { items: NamingItem[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((i) => (
        <Row
          key={i.issue_key}
          keyText={i.issue_key}
          url={i.jira_url}
          title={i.issue_name}
          meta={[i.issue_type, i.status, `created ${fmt.dayMonth(i.created_at)}`].filter(Boolean).map((t, n) => (
            <React.Fragment key={n}>
              {n > 0 && <Dot />}
              <span>{t}</span>
            </React.Fragment>
          ))}
        >
          <ul className="mt-2 space-y-1.5">
            {i.findings.map((f) => (
              <li key={f.rule} className="text-xs text-gray-700">
                <span>{f.message}</span>
                {f.suggestion && <span className="mt-0.5 block text-blue-900">Try: {f.suggestion}</span>}
              </li>
            ))}
          </ul>
        </Row>
      ))}
    </ul>
  );
}

export function FlowList({ items }: { items: FlowItem[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((i) => (
        <Row
          key={i.id}
          keyText={i.issue_key}
          url={i.jira_url}
          title={i.issue_name}
          hint={i.hint}
          meta={
            <>
              <span className="inline-flex items-center gap-1">
                {i.from}
                <ArrowRight className="h-3 w-3" aria-hidden />
                <span className="sr-only">to</span>
                {i.to}
              </span>
              <Dot />
              <span>{fmt.dayMonth(i.at)}</span>
            </>
          }
        />
      ))}
    </ul>
  );
}

function EntryRow({ e }: { e: TimelogEntryItem }) {
  return (
    <Row
      keyText={e.issue_key}
      url={e.jira_url}
      title={e.issue_name}
      hint={e.hint}
      meta={
        <>
          <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${PROBLEM_TONE[e.problem]}`}>{PROBLEM_LABEL[e.problem]}</span>
          <span>{fmt.dayMonth(e.started)}</span>
          <Dot />
          <span>{fmt.hours(e.hours)}</span>
        </>
      }
    >
      {e.comment && <p className="mt-1.5 truncate rounded-md bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700">{e.comment}</p>}
    </Row>
  );
}

function NoTimeRow({ t }: { t: NoTimeItem }) {
  return (
    <Row
      keyText={t.issue_key}
      url={t.jira_url}
      title={t.issue_name}
      hint={t.hint}
      meta={
        <>
          <span>In progress for {t.days} {t.days === 1 ? 'day' : 'days'} with no time logged</span>
          {t.stale && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[0.6875rem] font-medium text-gray-600">Probably not under way</span>
          )}
        </>
      }
    />
  );
}

export function TimelogList({ data }: { data: TimelogData }) {
  return (
    <div className="space-y-5">
      {data.entries.length > 0 && (
        <section aria-label="Entries to fix">
          <h3 className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Entries to fix · {data.entries.length}</h3>
          <ul className="space-y-2.5">
            {data.entries.map((e) => (
              <EntryRow key={e.id} e={e} />
            ))}
          </ul>
        </section>
      )}
      {data.no_time.items.length > 0 && (
        <section aria-label="In progress with no time">
          <h3 className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">In progress with no time · {data.no_time.items.length}</h3>
          <ul className="space-y-2.5">
            {data.no_time.items.map((t) => (
              <NoTimeRow key={t.issue_key} t={t} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function PlanningRow({ i }: { i: PlanningItem }) {
  return (
    <Row
      keyText={i.issue_key}
      url={i.jira_url}
      title={i.issue_name}
      hint={i.hint}
      meta={
        <>
          {i.status && <span>{i.status}</span>}
          {i.status && <Dot />}
          <span>Carried over {i.rollover_count} {i.rollover_count === 1 ? 'time' : 'times'}</span>
          {i.rollover_reason && (
            <>
              <Dot />
              <span>{i.rollover_reason}</span>
            </>
          )}
        </>
      }
    />
  );
}

export function PlanningList({ data }: { data: PlanningData }) {
  return (
    <ul className="space-y-2.5">
      {data.items.map((i) => (
        <PlanningRow key={i.issue_key} i={i} />
      ))}
    </ul>
  );
}
